import { useEffect, useRef, useState } from "react";
import { Dithering } from "@paper-design/shaders-react";
import Globe from "./Globe";

// ───────────────────────────────────────────────────────────────
// Mark — a slow 1-bit sphere, the only ornament on the page.
// It's drawn in ink at rest and borrows the hovered project's color,
// easing between them so color arrives and leaves rather than cuts.
// A project with a `route` turns it into a globe for as long as it's
// hovered (see Globe).
// ───────────────────────────────────────────────────────────────

const toRgb = (hex) =>
  hex
    .replace("#", "")
    .match(/\w\w/g)
    .map((x) => parseInt(x, 16));
const toHex = (rgb) =>
  `#${rgb.map((x) => Math.round(x).toString(16).padStart(2, "0")).join("")}`;

export default function Mark({ ink, paper, color, route, size = 56 }) {
  const target = color || ink;
  const [shown, setShown] = useState(target);
  const cur = useRef(toRgb(target));

  useEffect(() => {
    const goal = toRgb(target);
    let raf;
    const step = () => {
      cur.current = cur.current.map((v, i) => v + (goal[i] - v) * 0.1);
      const done = cur.current.every((v, i) => Math.abs(v - goal[i]) < 0.5);
      if (done) cur.current = goal;
      setShown(toHex(cur.current));
      if (!done) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);

  return (
    <div className="mark" style={{ width: size, height: size }} aria-hidden>
      <Dithering
        shape="sphere"
        type="4x4"
        size={2}
        speed={0.35}
        scale={0.9}
        colorBack={paper}
        colorFront={shown}
        style={{ width: "100%", height: "100%", opacity: route ? 0 : 1, transition: "opacity 0.35s ease" }}
      />
      <Globe route={route} color={shown} size={size} />
    </div>
  );
}
